import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import * as faceapi from "face-api.js";
import { attendanceAPI, usersAPI } from "../utils/api";
import { CheckCircle, XCircle, Loader, LogIn, LogOut, X, ChevronLeft } from "lucide-react";
import { fmtTime } from "../utils/dateUtils";

const MODEL_URL = "/models";
const LOGO_URL = "https://customer-assets.emergentagent.com/job_9e7e0d6e-4f16-4092-b7c7-18a05794ec67/artifacts/w0k6jgr2_WhatsApp%20Image%202026-04-07%20at%2017.12.34.jpeg";

// Kitchen stored in localStorage so tablet remembers its kitchen
const KITCHEN_KEY = "kiosk_kitchen_id";

function useClock() {
  const [time, setTime] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  return time;
}

export default function KioskPage() {
  const navigate = useNavigate();
  const videoRef = useRef();
  const canvasRef = useRef();
  const loopRef = useRef(null);
  const time = useClock();

  // Kitchen
  const [kitchens, setKitchens] = useState([]);
  const [kitchenId, setKitchenId] = useState(() => localStorage.getItem(KITCHEN_KEY) || "");
  const [kitchenName, setKitchenName] = useState("");
  const [showKitchenPicker, setShowKitchenPicker] = useState(false);

  // Models
  const [modelsLoaded, setModelsLoaded] = useState(false);
  const [modelsLoading, setModelsLoading] = useState(false);

  // Camera
  const [cameraReady, setCameraReady] = useState(false);

  // Kiosk state
  const [phase, setPhase] = useState("idle"); // idle | scanning | recognized | unknown | processing | feedback
  const [matchResult, setMatchResult] = useState(null);
  const [feedbackMsg, setFeedbackMsg] = useState({ type: "", text: "" });
  const [todaySummary, setTodaySummary] = useState(null);
  const [showExit, setShowExit] = useState(false);

  // ── Load kitchens
  useEffect(() => {
    usersAPI.getKitchens().then(r => {
      const ks = r.data || [];
      setKitchens(ks);
      if (!kitchenId && ks.length > 0) {
        const id = ks[0].id;
        setKitchenId(id);
        localStorage.setItem(KITCHEN_KEY, id);
      }
      const found = ks.find(k => k.id === (kitchenId || ks[0]?.id));
      if (found) setKitchenName(found.name);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const found = kitchens.find(k => k.id === kitchenId);
    if (found) setKitchenName(found.name);
  }, [kitchenId, kitchens]);

  // ── Load models
  useEffect(() => { loadModels(); }, []);

  const loadModels = async () => {
    if (faceapi.nets.tinyFaceDetector.isLoaded) { setModelsLoaded(true); return; }
    setModelsLoading(true);
    try {
      await Promise.all([
        faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL),
        faceapi.nets.faceLandmark68TinyNet.loadFromUri(MODEL_URL),
        faceapi.nets.faceRecognitionNet.loadFromUri(MODEL_URL),
      ]);
      setModelsLoaded(true);
    } catch (e) { if (process.env.NODE_ENV === 'development') console.error('Fetch error:', e); }
    setModelsLoading(false);
  };

  // ── Start camera once models are ready
  useEffect(() => {
    if (modelsLoaded && !cameraReady) startCamera();
  }, [modelsLoaded]);

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: { ideal: 1280 }, height: { ideal: 720 }, facingMode: "user" }
      });
      videoRef.current.srcObject = stream;
      await videoRef.current.play();
      setCameraReady(true);
      startLoop();
    } catch (e) { if (process.env.NODE_ENV === 'development') console.error('Fetch error:', e); }
  };

  useEffect(() => () => stopLoop(), []);

  const startLoop = () => {
    if (loopRef.current) return;
    loopRef.current = setInterval(scanFrame, 1800);
  };

  const stopLoop = () => {
    if (loopRef.current) { clearInterval(loopRef.current); loopRef.current = null; }
  };

  // ── Fetch today summary
  const fetchSummary = useCallback(async () => {
    if (!kitchenId) return;
    try {
      const r = await attendanceAPI.getTodaySummary(kitchenId);
      setTodaySummary(r.data);
    } catch (e) { if (process.env.NODE_ENV === 'development') console.error('Fetch error:', e); }
  }, [kitchenId]);

  useEffect(() => {
    fetchSummary();
    const t = setInterval(fetchSummary, 30000);
    return () => clearInterval(t);
  }, [fetchSummary]);

  // ── Scan frame
  const scanFrame = async () => {
    if (!videoRef.current || !modelsLoaded) return;
    const video = videoRef.current;
    if (!video.srcObject || video.readyState < 2) return;
    if (phase !== "idle" && phase !== "scanning") return;

    setPhase("scanning");
    try {
      const det = await faceapi
        .detectSingleFace(video, new faceapi.TinyFaceDetectorOptions({ inputSize: 224, scoreThreshold: 0.5 }))
        .withFaceLandmarks(true)
        .withFaceDescriptor();

      if (!det) { setPhase("idle"); return; }

      // Draw bounding box
      if (canvasRef.current) {
        canvasRef.current.width = video.videoWidth;
        canvasRef.current.height = video.videoHeight;
        const ctx = canvasRef.current.getContext("2d");
        ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
        ctx.strokeStyle = "#E8956D"; ctx.lineWidth = 3;
        const { x, y, width, height } = det.detection.box;
        ctx.strokeRect(x, y, width, height);
      }

      const descriptor = Array.from(det.descriptor);

      // Capture image
      const imgCanvas = document.createElement("canvas");
      imgCanvas.width = video.videoWidth; imgCanvas.height = video.videoHeight;
      imgCanvas.getContext("2d").drawImage(video, 0, 0);
      const imgB64 = imgCanvas.toDataURL("image/jpeg", 0.75);

      if (!kitchenId) { setPhase("idle"); return; }

      const res = await attendanceAPI.recognize({ face_descriptor: descriptor, kitchen_id: kitchenId, image_base64: imgB64 });
      const data = res.data;

      if (data.matched) {
        stopLoop();
        setMatchResult({ ...data, imgB64 });
        setPhase("recognized");
      } else {
        setPhase("unknown");
        setTimeout(() => setPhase("idle"), 2200);
      }
    } catch { setPhase("idle"); }
  };

  // ── Check in / out
  const handleAction = async (type) => {
    if (!matchResult) return;
    setPhase("processing");
    try {
      if (type === "in") {
        const r = await attendanceAPI.checkIn({
          staff_id: matchResult.staff_id,
          kitchen_id: kitchenId,
          image_base64: matchResult.imgB64,
        });
        if (r.data.already_checked_in) {
          showFeedback("warning", `${matchResult.staff_name} is already checked in`);
        } else {
          showFeedback("success", r.data.is_late
            ? `${matchResult.staff_name} — Checked In (Late)`
            : `${matchResult.staff_name} — Checked In`);
        }
      } else {
        const r = await attendanceAPI.checkOut({
          staff_id: matchResult.staff_id,
          kitchen_id: kitchenId,
          image_base64: matchResult.imgB64,
        });
        showFeedback("success", `${matchResult.staff_name} — Checked Out · ${r.data.hours_worked}h`);
      }
      fetchSummary();
    } catch (e) {
      showFeedback("error", "Could not record attendance. Try again.");
    }
    setMatchResult(null);
  };

  const showFeedback = (type, text) => {
    setFeedbackMsg({ type, text });
    setPhase("feedback");
    setTimeout(() => {
      setPhase("idle");
      setFeedbackMsg({ type: "", text: "" });
      startLoop();
    }, 3500);
  };

  const dismiss = () => {
    setMatchResult(null);
    setPhase("idle");
    startLoop();
  };

  // ── Kitchen picker
  const pickKitchen = (id) => {
    setKitchenId(id);
    localStorage.setItem(KITCHEN_KEY, id);
    setShowKitchenPicker(false);
  };

  const scanRingColor = phase === "recognized" ? "#637C64" : phase === "unknown" ? "#C2624E" : phase === "scanning" ? "#E8956D" : "#333";

  return (
    <div className="fixed inset-0 bg-[#0f0f0f] flex flex-col overflow-hidden" data-testid="kiosk-page">
      {/* ── TOP BAR ─────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between px-6 py-3 bg-[#1a1a1a] border-b border-white/10 flex-shrink-0">
        <div className="flex items-center gap-3">
          <img src={LOGO_URL} alt="Manna" className="w-9 h-9 rounded-lg object-contain" />
          <div>
            <p className="text-white font-bold text-sm leading-tight">Manna Trust</p>
            <button
              onClick={() => setShowKitchenPicker(true)}
              className="text-white/50 text-xs hover:text-white/80 transition-colors"
            >
              {kitchenName || "Select Kitchen"} ▾
            </button>
          </div>
        </div>

        {/* Clock */}
        <div className="text-center">
          <p className="text-white text-2xl font-mono font-bold tabular-nums">
            {time.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
          </p>
          <p className="text-white/40 text-xs">
            {time.toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long" })}
          </p>
        </div>

        {/* Exit */}
        <button
          onClick={() => setShowExit(true)}
          className="text-white/20 hover:text-white/50 transition-colors p-2 rounded-lg"
          data-testid="kiosk-exit-btn"
        >
          <ChevronLeft size={20} />
        </button>
      </div>

      {/* ── MAIN AREA ───────────────────────────────────────────────────── */}
      <div className="flex-1 flex flex-col items-center justify-center relative overflow-hidden px-4 py-4 min-h-0">

        {/* Loading state */}
        {(modelsLoading || (!modelsLoaded && !cameraReady)) && (
          <div className="absolute inset-0 flex items-center justify-center bg-[#0f0f0f] z-20">
            <div className="text-center">
              <Loader size={36} className="animate-spin text-[#E8956D] mx-auto mb-4" />
              <p className="text-white/60 text-sm">Loading face recognition models…</p>
            </div>
          </div>
        )}

        {/* Camera + overlay */}
        <div
          className="relative w-full max-w-2xl rounded-2xl overflow-hidden"
          style={{ border: `3px solid ${scanRingColor}`, transition: "border-color 0.3s" }}
        >
          <video
            ref={videoRef}
            autoPlay muted playsInline
            className="w-full block"
            style={{ transform: "scaleX(-1)", maxHeight: "60vh", objectFit: "cover", background: "#000" }}
          />
          <canvas
            ref={canvasRef}
            className="absolute inset-0 w-full h-full pointer-events-none"
            style={{ transform: "scaleX(-1)" }}
          />

          {/* Scan pulse ring */}
          {phase === "scanning" && (
            <div className="absolute inset-0 pointer-events-none">
              <div className="absolute inset-0 border-4 border-[#E8956D]/40 rounded-2xl animate-ping" style={{ animationDuration: "1.5s" }} />
            </div>
          )}

          {/* Status chip */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2">
            {phase === "idle" && cameraReady && (
              <span className="bg-black/60 text-white/70 text-sm px-4 py-1.5 rounded-full backdrop-blur-sm">
                Looking for a face…
              </span>
            )}
            {phase === "scanning" && (
              <span className="bg-[#E8956D]/80 text-[#3A322C] text-sm font-bold px-4 py-1.5 rounded-full flex items-center gap-1.5 backdrop-blur-sm">
                <Loader size={13} className="animate-spin" /> Recognising…
              </span>
            )}
            {phase === "unknown" && (
              <span className="bg-[#C2624E]/90 text-white text-sm font-bold px-4 py-1.5 rounded-full flex items-center gap-1.5">
                <XCircle size={14} /> Face not registered
              </span>
            )}
          </div>
        </div>

        {/* ── RECOGNIZED CARD ──────────────────────────────────────────── */}
        {phase === "recognized" && matchResult && (
          <div className="w-full max-w-2xl mt-4 bg-[#1c1c1c] border border-white/10 rounded-2xl p-5 animate-fade-in-up flex-shrink-0">
            <div className="flex items-center gap-4 mb-5">
              {matchResult.face_image ? (
                <img src={matchResult.face_image} alt={matchResult.staff_name}
                  className="w-16 h-16 rounded-full object-cover border-2 border-[#637C64] flex-shrink-0" />
              ) : (
                <div className="w-16 h-16 rounded-full bg-[#E8956D]/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-2xl font-bold text-[#E8956D]">{matchResult.staff_name?.[0]}</span>
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-white text-2xl font-extrabold font-[Manrope] truncate">{matchResult.staff_name}</p>
                <p className="text-white/50 text-sm">{matchResult.employee_id}{matchResult.designation ? ` · ${matchResult.designation}` : ""}</p>
                <div className="flex items-center gap-1 mt-1">
                  <CheckCircle size={12} className="text-[#637C64]" />
                  <span className="text-[#637C64] text-xs font-semibold">{Math.round(matchResult.confidence * 100)}% match</span>
                </div>
              </div>
              <button onClick={dismiss} className="text-white/30 hover:text-white/60 flex-shrink-0"><X size={20} /></button>
            </div>

            {/* Today's status + action buttons */}
            {matchResult.today_status === "not_checked_in" && (
              <div className="space-y-3">
                <p className="text-white/40 text-sm text-center">Not yet checked in today</p>
                <button
                  onClick={() => handleAction("in")}
                  className="w-full bg-[#637C64] hover:bg-[#4e6350] text-white rounded-xl py-5 font-bold text-xl flex items-center justify-center gap-3 transition-colors"
                  data-testid="kiosk-checkin-btn"
                >
                  <LogIn size={26} /> Check In
                </button>
              </div>
            )}

            {matchResult.today_status === "checked_in" && (
              <div className="space-y-3">
                {matchResult.today_record?.check_in && (
                  <p className="text-white/40 text-sm text-center">
                    Checked in at {fmtTime(matchResult.today_record.check_in)}
                  </p>
                )}
                <button
                  onClick={() => handleAction("out")}
                  className="w-full bg-[#C2624E] hover:bg-[#a85040] text-white rounded-xl py-5 font-bold text-xl flex items-center justify-center gap-3 transition-colors"
                  data-testid="kiosk-checkout-btn"
                >
                  <LogOut size={26} /> Check Out
                </button>
              </div>
            )}

            {matchResult.today_status === "checked_out" && (
              <div className="bg-[#637C64]/20 border border-[#637C64]/40 rounded-xl px-4 py-4 flex items-center justify-center gap-2">
                <CheckCircle size={18} className="text-[#637C64]" />
                <p className="text-[#637C64] font-bold">Attendance complete for today</p>
                {matchResult.today_record?.hours_worked && (
                  <span className="text-[#637C64]/70 text-sm ml-1">· {matchResult.today_record.hours_worked}h worked</span>
                )}
              </div>
            )}
          </div>
        )}

        {/* ── PROCESSING ─────────────────────────────────────────────────── */}
        {phase === "processing" && (
          <div className="w-full max-w-2xl mt-4 bg-[#1c1c1c] border border-white/10 rounded-2xl p-8 flex items-center justify-center gap-3 flex-shrink-0">
            <Loader size={24} className="animate-spin text-[#E8956D]" />
            <span className="text-white/70 text-lg font-medium">Recording attendance…</span>
          </div>
        )}

        {/* ── FEEDBACK ────────────────────────────────────────────────────── */}
        {phase === "feedback" && (
          <div className={`w-full max-w-2xl mt-4 rounded-2xl p-6 flex items-center gap-4 flex-shrink-0 animate-fade-in-up ${
            feedbackMsg.type === "success" ? "bg-[#637C64]/20 border border-[#637C64]/50"
            : feedbackMsg.type === "warning" ? "bg-[#D98032]/20 border border-[#D98032]/50"
            : "bg-[#C2624E]/20 border border-[#C2624E]/50"
          }`}>
            {feedbackMsg.type === "success"
              ? <CheckCircle size={32} className="text-[#637C64] flex-shrink-0" />
              : <XCircle size={32} className="text-[#C2624E] flex-shrink-0" />
            }
            <p className={`text-xl font-bold font-[Manrope] ${
              feedbackMsg.type === "success" ? "text-[#637C64]"
              : feedbackMsg.type === "warning" ? "text-[#D98032]"
              : "text-[#C2624E]"
            }`}>{feedbackMsg.text}</p>
          </div>
        )}
      </div>

      {/* ── BOTTOM BAR ──────────────────────────────────────────────────── */}
      {todaySummary && (
        <div className="bg-[#1a1a1a] border-t border-white/10 px-6 py-3 flex-shrink-0">
          <div className="flex items-center justify-center gap-8">
            {[
              { label: "Total", value: todaySummary.total_staff, color: "text-white/60" },
              { label: "Present", value: todaySummary.present, color: "text-[#637C64]" },
              { label: "Absent", value: todaySummary.absent, color: "text-[#C2624E]" },
              { label: "Late", value: todaySummary.late_count, color: "text-[#D98032]" },
            ].map(s => (
              <div key={s.label} className="text-center">
                <p className={`text-xl font-extrabold font-[Manrope] ${s.color}`}>{s.value}</p>
                <p className="text-white/30 text-[10px] uppercase tracking-wider">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── KITCHEN PICKER MODAL ────────────────────────────────────────── */}
      {showKitchenPicker && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-6">
          <div className="bg-[#1c1c1c] border border-white/10 rounded-2xl p-6 w-full max-w-sm">
            <h3 className="text-white font-bold text-lg mb-4">Select Kitchen</h3>
            <div className="space-y-2">
              {kitchens.map(k => (
                <button key={k.id} onClick={() => pickKitchen(k.id)}
                  className={`w-full text-left px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
                    k.id === kitchenId ? "bg-[#E8956D] text-[#3A322C]" : "bg-white/5 text-white hover:bg-white/10"
                  }`}>
                  {k.name}
                </button>
              ))}
            </div>
            <button onClick={() => setShowKitchenPicker(false)} className="mt-4 w-full border border-white/10 rounded-xl py-2.5 text-white/50 hover:text-white text-sm">Cancel</button>
          </div>
        </div>
      )}

      {/* ── EXIT CONFIRMATION ───────────────────────────────────────────── */}
      {showExit && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-6">
          <div className="bg-[#1c1c1c] border border-white/10 rounded-2xl p-6 w-full max-w-xs text-center">
            <p className="text-white font-bold text-lg mb-2">Exit Kiosk?</p>
            <p className="text-white/40 text-sm mb-6">This will return to the main application</p>
            <div className="flex gap-3">
              <button onClick={() => setShowExit(false)} className="flex-1 border border-white/10 rounded-xl py-3 text-white/60 hover:text-white text-sm font-bold">Stay</button>
              <button onClick={() => navigate("/attendance")} className="flex-1 bg-[#E8956D] rounded-xl py-3 text-[#3A322C] text-sm font-bold">Exit</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
